import React, { useState } from "react";
import { toast } from "sonner";
import {
  Send,
  MapPin,
  Clock,
  Phone,
  Mail,
  MessageCircle,
  Instagram,
  Music,
  Facebook,
  Youtube,
  CheckCircle2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ConsentBlock from "@/components/ConsentBlock";
import SEO from "@/components/SEO";
import { useSiteSettings } from "@/hooks/useSiteSettings";

/* ------------------------------------------------ page */

export default function Contact() {
  const settings = useSiteSettings();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", subject: "", message: "" });
  const [consent, setConsent] = useState({ required_agreement: false, marketing_email: false, marketing_sms: false });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent.required_agreement) {
      toast.error("Please confirm the required consent so we can reply to you.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", { ...form, consent });
      toast.success("Message sent — we'll reply soon.");
      setForm({ full_name: "", email: "", phone: "", subject: "", message: "" });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Build the visible-channels list — every entry only renders when configured.
  const channels = buildChannels(settings);

  const rawMap = (settings.google_maps_url || "").trim();
  const mapEmbed = normalizeMapEmbed(rawMap, settings.business_address);

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact"
        description={`Get in touch with ${settings.business_name} — Panini collectors' HQ in the United States.`}
      />

      <section className="hero-black">
        <div className="container-gm py-14 md:py-20">
          <span className="sticker sticker-white">SAY HELLO</span>
          <h1 className="h-display text-white text-5xl md:text-7xl mt-6" data-testid="contact-title">
            Let&apos;s talk<br /><span className="text-[var(--panini-yellow)]">stickers.</span>
          </h1>
          <p className="text-white/85 mt-6 max-w-xl leading-relaxed">
            Question, custom order, or event idea? Send a note — we read every one.
          </p>
          <p className="text-white/60 mt-3 max-w-xl text-sm leading-relaxed">
            Looking for specific stickers? The{" "}
            <a href="/order-missing" className="text-[var(--panini-yellow)] underline underline-offset-4">
              Order Missing Stickers
            </a>{" "}
            form gets you a quote faster than this one.
          </p>
        </div>
        <div className="field-line" aria-hidden />
      </section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 2 · Quick Response — Instagram + WhatsApp primary
          contact cards. Placed immediately below the hero so the
          fastest channels are the first thing visitors see.
          ═══════════════════════════════════════════════════════════ */}
      <section className="container-gm py-12 md:py-16" data-testid="contact-quick-response">
        <div className="max-w-3xl">
          <div className="kicker kicker-red">The fastest way</div>
          <h2 className="h-display text-3xl md:text-4xl mt-2">
            Need a quick response?
          </h2>
          <p className="text-[var(--smoke)] mt-3 leading-relaxed">
            The fastest way to reach us is through Instagram or WhatsApp.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mt-8">
          <QuickChannelCard
            variant="instagram"
            title="Instagram DM"
            eyebrow="Primary Contact"
            handle={settings.instagram_url ? displayHandle(settings.instagram_url) : "@GilMaakaroun"}
            helper="Usually the fastest response."
            action="Message on Instagram"
            href={settings.instagram_url || "https://instagram.com/gilmaakaroun"}
            testid="contact-quick-instagram"
          />
          <QuickChannelCard
            variant="whatsapp"
            title="WhatsApp"
            eyebrow="Secondary Contact"
            handle={settings.whatsapp_display || settings.business_phone || "Business phone"}
            helper="Perfect for sending missing sticker lists."
            action="Message on WhatsApp"
            href={settings.whatsapp_url || null}
            testid="contact-quick-whatsapp"
          />
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 3 · Other Ways To Reach Us — every existing channel
          + business info + map. Moved BELOW the quick-response cards
          so the primary channels stay dominant.
          ═══════════════════════════════════════════════════════════ */}
      <section className="container-gm py-12 md:py-16" data-testid="contact-other-ways">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-12" data-testid="contact-info-panel">
            <div>
              <div className="kicker kicker-red">Where else to find us</div>
              <h2 className="h-display text-3xl md:text-4xl mt-2">
                Other ways<br />to reach us.
              </h2>
              <p className="text-[var(--smoke)] mt-3 text-sm leading-relaxed max-w-md">
                Pick whatever&apos;s easiest — everything below lands with Gil.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="contact-channels">
              {channels.map((c) => (
                <ContactChannel key={c.label} {...c} />
              ))}
            </div>

            {mapEmbed && (
              <div
                className="rounded-2xl border-2 border-[var(--ink)] overflow-hidden bg-white mt-6"
                data-testid="contact-map"
              >
                <div className="p-4 border-b-2 border-[var(--ink)] flex items-center justify-between">
                  <div>
                    <div className="kicker kicker-red">Find us</div>
                    <div className="font-display text-lg mt-0.5">
                      {settings.business_address || settings.business_name}
                    </div>
                  </div>
                  <MapPin size={20} aria-hidden />
                </div>
                <iframe
                  title={`${settings.business_name} location`}
                  src={mapEmbed}
                  className="w-full h-64 border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 4 · Email contact form — last resort, kept fully
          functional. Same POST /api/contact endpoint (no backend
          changes in this phase).
          ═══════════════════════════════════════════════════════════ */}
      <section className="container-gm pb-16 md:pb-24" data-testid="contact-form-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <div className="kicker kicker-red">Prefer email?</div>
            <h2 className="h-display text-3xl md:text-4xl mt-2">
              Send us a message.
            </h2>
            <p className="text-[var(--smoke)] mt-3 leading-relaxed">
              For everything else, drop us a note and Gil will get back to you.
            </p>
          </div>
          {sent ? (
              <div
                className="card-fun p-8 md:p-10 bg-[var(--panini-yellow)]"
                data-testid="contact-success"
                role="status"
                aria-live="polite"
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-2xl border-2 border-[var(--ink)] bg-[var(--field-green)] text-white flex items-center justify-center">
                    <CheckCircle2 size={22} aria-hidden />
                  </div>
                  <div>
                    <div className="kicker kicker-red">Message received</div>
                    <h3 className="h-display text-3xl md:text-4xl mt-2">Thanks — we&apos;ll be in touch.</h3>
                    <p className="text-[var(--ink)]/85 mt-3 max-w-md leading-relaxed">
                      Same-day reply on most requests. Meanwhile, feel free to browse the store or send us your missing-sticker list.
                    </p>
                    <div className="flex flex-wrap gap-3 mt-6">
                      <a href="/shop" className="btn-black" data-testid="contact-success-shop">Browse the store</a>
                      <a href="/order-missing" className="btn-red" data-testid="contact-success-missing">Send my missing list</a>
                      <button
                        type="button"
                        onClick={() => setSent(false)}
                        className="btn-ghost"
                        data-testid="contact-success-another"
                      >
                        Send another message
                      </button>
                    </div>
                  </div>
                </div>
              </div>
          ) : (
              <form onSubmit={submit} className="card-fun p-8 md:p-10 space-y-5" data-testid="contact-form">
              <Row2>
                <Field label="Full name" required>
                  <input required data-testid="contact-name" value={form.full_name} onChange={update("full_name")} className="gm-field" />
                </Field>
                <Field label="Email" required>
                  <input required type="email" data-testid="contact-email" value={form.email} onChange={update("email")} className="gm-field" />
                </Field>
              </Row2>
              <Row2>
                <Field label="Phone (optional)">
                  <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} className="gm-field" />
                </Field>
                <Field label="Subject (optional)">
                  <input data-testid="contact-subject" value={form.subject} onChange={update("subject")} className="gm-field" />
                </Field>
              </Row2>
              <Field label="Message" required>
                <textarea required rows={6} data-testid="contact-message" value={form.message} onChange={update("message")} className="gm-field" />
              </Field>

              <ConsentBlock value={consent} onChange={setConsent} settings={settings} prefix="contact-consent" />

              <button type="submit" disabled={busy} data-testid="contact-submit" className="btn-red disabled:opacity-60">
                <Send size={16} aria-hidden /> {busy ? "Sending…" : "Send message"}
              </button>
              <p className="text-[var(--smoke)] text-xs">
                Same-day reply on most requests. For a missing-sticker request, please use the
                dedicated form so it lands in the correct queue.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------ helpers */

// Batch V1 · Phase 4A — big Instagram + WhatsApp contact cards used
// in the "Need a quick response?" section. Visually distinct from the
// small `ContactChannel` tiles further down. Falls back gracefully to
// a subdued "not configured" state when the site setting is blank.
function QuickChannelCard({ variant, title, eyebrow, handle, helper, action, href, testid }) {
  const isIG = variant === "instagram";
  const cardCls = isIG
    ? "bg-gradient-to-br from-[#fef9e7] via-white to-[#ffe1e6] border-[var(--panini-red)]"
    : "bg-gradient-to-br from-emerald-50 via-white to-emerald-100 border-emerald-700";
  const iconWrapCls = isIG
    ? "bg-gradient-to-br from-[#f58529] via-[#dd2a7b] to-[#8134af] text-white border-[var(--ink)]"
    : "bg-emerald-500 text-white border-emerald-800";
  const btnCls = isIG
    ? "bg-[var(--panini-red)] text-white border-[var(--ink)] shadow-[4px_4px_0_var(--ink)] hover:-translate-y-0.5"
    : "bg-emerald-600 text-white border-emerald-900 shadow-[4px_4px_0_emerald-900] hover:-translate-y-0.5";
  const disabled = !href;
  return (
    <div
      className={`rounded-3xl border-4 ${cardCls} p-6 md:p-8 shadow-[6px_6px_0_var(--ink)] flex flex-col`}
      data-testid={testid}
    >
      <div className="flex items-start gap-4">
        <div className={`shrink-0 w-14 h-14 rounded-2xl border-2 ${iconWrapCls} grid place-items-center shadow-[3px_3px_0_var(--ink)]`}>
          {isIG ? <Instagram size={26} aria-hidden /> : <MessageCircle size={26} aria-hidden />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--smoke)] font-bold">
            {eyebrow}
          </div>
          <h3 className="h-display text-2xl md:text-3xl mt-1 leading-tight">{title}</h3>
          <div className="font-mono text-sm md:text-base mt-1 text-[var(--ink)]/80 truncate" title={handle}>
            {handle}
          </div>
        </div>
      </div>
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
        {disabled ? (
          <span
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-dashed border-[var(--ink)]/40 px-5 py-3 text-xs font-black uppercase tracking-widest text-[var(--smoke)] bg-white/60"
            aria-disabled="true"
          >
            {action} · coming soon
          </span>
        ) : (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center justify-center gap-2 rounded-full border-2 ${btnCls} px-5 py-3 text-xs font-black uppercase tracking-widest transition-transform focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--panini-yellow)]`}
            data-testid={`${testid}-action`}
            aria-label={`${action} at ${handle}`}
          >
            {isIG ? <Instagram size={14} aria-hidden /> : <MessageCircle size={14} aria-hidden />}
            {action}
          </a>
        )}
        <p className="text-xs text-[var(--ink)]/70 sm:ml-2">
          {helper}
        </p>
      </div>
    </div>
  );
}


function buildChannels(s) {
  const list = [];
  if (s.business_address)
    list.push({ Icon: MapPin, label: "Address", value: s.business_address, href: null, color: "yellow" });
  if (s.business_hours)
    list.push({ Icon: Clock, label: "Hours", value: s.business_hours, href: null, color: "green" });
  if (s.contact_phone)
    list.push({ Icon: Phone, label: "Phone", value: s.contact_phone, href: `tel:${s.contact_phone.replace(/\s+/g, "")}`, color: "red" });
  if (s.contact_email)
    list.push({ Icon: Mail, label: "Email", value: s.contact_email, href: `mailto:${s.contact_email}`, color: "black" });
  if (s.whatsapp_url)
    list.push({ Icon: MessageCircle, label: "WhatsApp", value: "Message on WhatsApp", href: s.whatsapp_url, color: "green" });
  if (s.instagram_url)
    list.push({ Icon: Instagram, label: "Instagram", value: displayHandle(s.instagram_url), href: s.instagram_url, color: "red" });
  if (s.tiktok_url)
    list.push({ Icon: Music, label: "TikTok", value: displayHandle(s.tiktok_url), href: s.tiktok_url, color: "yellow" });
  if (s.facebook_url)
    list.push({ Icon: Facebook, label: "Facebook", value: "Follow on Facebook", href: s.facebook_url, color: "black" });
  if (s.youtube_url)
    list.push({ Icon: Youtube, label: "YouTube", value: "Watch on YouTube", href: s.youtube_url, color: "red" });
  return list;
}

function displayHandle(url) {
  try {
    const u = new URL(url);
    const last = u.pathname.split("/").filter(Boolean).pop();
    return last ? `@${last.replace(/^@/, "")}` : url;
  } catch {
    return url;
  }
}

/** Convert a plain maps URL to an embeddable URL, or fall back to an address query. */
function normalizeMapEmbed(url, address) {
  const trimmed = (url || "").trim();
  if (trimmed) {
    if (/\/maps\/embed/.test(trimmed)) return trimmed;
    if (trimmed.includes("maps.app.goo.gl") || /\/maps/.test(trimmed)) {
      // Fall back to a search embed using the same string, works reliably.
      return `https://www.google.com/maps?q=${encodeURIComponent(trimmed)}&output=embed`;
    }
    return trimmed;
  }
  const q = (address || "").trim();
  if (!q) return "";
  return `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;
}

function ContactChannel({ Icon, label, value, href, color }) {
  const map = {
    yellow: "bg-[var(--panini-yellow)] text-[var(--ink)]",
    green: "bg-[var(--field-green)] text-white",
    red: "bg-[var(--panini-red)] text-white",
    black: "bg-[var(--ink)] text-[var(--panini-yellow)]",
  };
  const inner = (
    <>
      <div className={`shrink-0 w-11 h-11 rounded-xl border-2 border-[var(--ink)] flex items-center justify-center ${map[color]}`}>
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <div className="kicker kicker-red text-[10px]">{label}</div>
        <div className="font-display text-sm mt-0.5 truncate">{value}</div>
      </div>
    </>
  );
  const className =
    "flex items-center gap-3 rounded-2xl border-2 border-[var(--ink)] bg-white p-4 hover:-translate-y-0.5 transition-transform";
  if (href) {
    return (
      <a
        href={href}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
        className={className}
        data-testid={`contact-channel-${label.toLowerCase()}`}
      >
        {inner}
      </a>
    );
  }
  return (
    <div className={className} data-testid={`contact-channel-${label.toLowerCase()}`}>
      {inner}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="kicker kicker-black mb-2 text-[11px]">
        {label} {required && <span className="text-[var(--panini-red)]">*</span>}
      </div>
      {children}
    </label>
  );
}
function Row2({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>;
}
